// ─────────────────────────────────────────────────────────────
// RIDING INSTRUCTOR READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// Same structure as Martial Arts Kids Class Ready (modeled on Managers), with a NEW palette (espresso + arena sand,
// turquoise buttons with dark text, sunny yellow numbers, coral labels), a NEW display font (Bricolage Grotesque),
// NEW button copy and a NEW section (3 rider levels + safety rules), to test what converts.
// Promise (must match ads, checkout and the PDF): 52-week lesson plan + 10 complete 45-minute group lessons,
// $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Riding: flatwork and ground poles only, no jumping; helmets always; no promises about levels, shows or bookings; no testimonials.
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://riding-instructor-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'Riding Instructor Ready™ | Your Whole Year of Riding Lessons, Already Planned';
export const META_DESCRIPTION =
  'A 52-week lesson plan plus 10 complete, ready-to-teach 45-minute group riding lessons. Open this week’s page, prepare in 5 minutes and teach it today.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'RIDING INSTRUCTOR READY™';
/** Label for every button that scrolls to the offer section (new copy for this landing) */
export const ctaLabel = 'GET MY LESSONS PLANNED';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY LESSONS PLANNED · $27';
export const topBarMobileLabel = 'ALL 52 WEEKS · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR RIDING INSTRUCTORS & RIDING SCHOOL OWNERS',
  titleLead: 'YOUR WHOLE YEAR OF RIDING LESSONS,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 52-week lesson plan plus 10 complete, ready-to-teach 45-minute group lessons. Open this week’s page and teach it today.',
  mockupAlt: 'Riding Instructor Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE LESSONS' },
  { value: '45', label: 'MINUTE GROUP LESSONS' },
  { value: '3', label: 'RIDER LEVELS' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'THE NIGHT BEFORE VS. IN THE ARENA.',
  before: {
    label: 'BEFORE',
    when: 'The night before',
    quote: '“What do we work on tomorrow?”',
    items: ['The same 3 exercises again.', 'Beginners lost, confident riders bored.', 'Every group planned from a blank page.'],
  },
  after: {
    label: 'AFTER',
    when: 'In the arena',
    items: ['Open this week’s page.', 'Prepare in 5 minutes.', 'Teach one clear goal in 45 minutes.', 'Cool down and thank the horse.'],
  },
};

// ── 3. WHAT DOES YOUR RIDE NEED THIS WEEK (pick a need, see the lesson that is ready for it) ──
export const selector = {
  eyebrow: 'START WHERE YOUR RIDERS ARE',
  title: 'WHAT DOES YOUR RIDE NEED THIS WEEK?',
  sub: 'Pick a need and see the lesson that is ready for it.',
  startLabel: 'YOUR NEXT LESSON',
  tablistLabel: 'Lesson needs',
  columns: ['PAGE 1 · LESSON PLAN', 'PAGE 2 · TEACH IT', 'PAGE 3 · RIDE IT'],
  note: 'All 10 lessons follow this same 3-page structure. Adapt each one to your horses, your riding school’s safety rules and your riders.',
  items: [
    {
      tab: 'A safe start: mounting & position',
      n: 'LESSON 01 · WEEK 1',
      title: 'Safety, Mounting & a Correct Position',
      goal: 'The rider checks the girth and stirrups, mounts from the block, sits in the ear–shoulder–hip–heel line at halt and walk and dismounts safely.',
      open: ['Lesson goal, start week and what you need', 'Say this: “Helmet on, chin strap done. Check your girth, then the stirrups. Sit tall: ear, shoulder, hip, heel — one straight line.”', 'Horse care tip: check the girth before you mount and again after 5 minutes of walk'],
      activity: ['Numbered teaching steps with key points', 'A rider position diagram and FIX IT cards for the most common errors', 'Rider level cards: how to teach it at each level'],
      close: ['Exercises for the arena', 'A game or challenge to finish the ride', 'Cool-down talk and next week’s focus'],
    },
    {
      tab: 'Walk on, halt & steering',
      n: 'LESSON 02 · WEEK 5',
      title: 'Walk On, Halt & Steering',
      goal: 'The rider walks on with a light leg aid, halts with the seat and soft hands and steers through cones while looking where they want to go.',
      open: ['Lesson goal, start week and what you need', 'Say this: “Squeeze, and walk on. Sit tall, close your fingers, breathe out… and halt. Look where you want to go — your horse goes where your eyes go.”', 'Horse care tip: a soft stroke on the neck'],
      activity: ['Teaching steps: walk on, halt, turn, support, praise', 'FIX IT cues: “Squeeze, don’t kick!” · “Eyes up, look at the next cone!”', 'Rider level cards, safety box, pro tip and common mistake'],
      close: ['Steering exercises with cones', 'A game or challenge to finish the ride', 'Cool-down talk and next week’s focus'],
    },
    {
      tab: 'The first canter',
      n: 'LESSON 09 · WEEK 35',
      title: 'First Canter',
      goal: 'The rider prepares in the corner, asks for canter with clear aids, sits softly for a short canter and comes back to trot calmly, one rider at a time.',
      open: ['Lesson goal, start week and what you need', 'Say this: “In the corner: sit, inside leg on the girth, outside leg a little back, outside rein steady. Ask — and canter. Hold the strap, sit softly, look up.”', 'A horse care tip for after the canter work'],
      activity: ['The canter aids step by step', 'FIX IT cards for the most common canter errors', 'Rider level cards: only riders who are ready canter'],
      close: ['The canter exercise, one rider at a time', 'The rest of the ride works at walk and trot', 'Cool-down talk and next week’s focus'],
    },
    {
      tab: 'A calm level check',
      n: 'LESSON 10 · WEEK 40',
      title: 'Level Check Lesson',
      goal: 'The rider shows the year’s skills in a calm practice level check: tack check and mounting, position, transitions, a circle, a change of rein, ground poles, two-point and, for Level 3, a short canter.',
      open: ['Lesson goal, start week and what you need', 'Say this: “Today is a practice level check. Ride your best, stay calm, and if something goes wrong, take a breath and keep going.”', 'A 45-minute lesson block for the check'],
      activity: ['The level check pattern, step by step', 'How to score each skill', 'What to do with a nervous rider'],
      close: ['A practice level check sheet: 10 skills, scored 1 to 3', 'A feedback script, word for word', 'What to work on next'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE NEXT TO THE ARENA.',
  sub: 'Open any page and read it at full size: a complete lesson plan, the teaching steps, the first canter and the level check sheet.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: [
    'Lesson 02 · lesson plan',
    'Lesson 02 · teach it',
    'Lesson 06 · transitions',
    'Lesson 09 · first canter',
    'Lesson 10 · the final lesson',
    'Lesson 10 · level check sheet',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY LESSON',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST OPEN THE PAGE AND TEACH.',
  text: 'Every week of the year is mapped: the start of one of the 10 complete lessons, a lesson focus with a 10-minute exercise or game, or a level check. Each lesson gives you the plan, the teaching steps and the arena work in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 52-week plan.' },
    { title: 'PREPARE', text: 'Read the page and set up the arena in 5 minutes.' },
    { title: 'TEACH', text: 'Tack check, warm-up and one clear goal.' },
    { title: 'RIDE IT', text: 'The exercise, a game, then cool down and thank the horse.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE LESSON',
  features: [
    { icon: 'goal', title: 'One clear lesson goal', text: 'What your riders will do, the week to start it and everything you need.' },
    { icon: 'timer', title: 'Built for a 45-minute group lesson', text: 'Tack check & mount · warm-up · main exercise · game or challenge · cool-down & horse care.' },
    { icon: 'steps', title: 'Teaching steps + FIX IT cards', text: 'Numbered steps with key points, and the quick cue for the most common position errors.' },
    { icon: 'questions', title: 'Rider level cards', text: 'How to adapt the lesson for walk, walk-and-trot and canter riders in the same ride.' },
    { icon: 'quote', title: 'SAY THIS: your words', text: 'Short, calm instructor words for the lesson, plus a pro tip and a common mistake.' },
    { icon: 'action', title: 'Arena work + horse care', text: 'Exercises for the arena, a game or challenge, a horse care tip and a safety box.' },
  ],
} as const;

// ── 5b. RIDER LEVELS + SAFETY (new section in this landing) ──────────────────
export const levelsBand = {
  eyebrow: 'ONE RIDE, THREE LEVELS',
  title: 'EVERY LESSON WORKS FOR THE WHOLE RIDE.',
  sub: 'Beginners and confident riders in the same group? Every lesson has rider level cards, so each level gets the right task.',
  levels: [
    { tag: 'LEVEL 1', name: 'Walk', text: 'First lessons: mounting, position, walk on, halt and steering, with a helper on the lead when needed.' },
    { tag: 'LEVEL 2', name: 'Walk & trot', text: 'Rising trot, school figures, transitions and ground poles in rhythm.' },
    { tag: 'LEVEL 3', name: 'Walk, trot & canter', text: 'Two-point, sitting trot and a first canter, one rider at a time.' },
  ],
  safetyTitle: 'SAFETY IS BUILT INTO EVERY LESSON',
  safety: ['Helmets and boots, always', 'Tack check before mounting', 'One horse length or more', 'Left hand to left hand', 'One rider canters at a time', 'Flatwork and ground poles, no jumping'],
};

// ── 6. THE 52-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 52-WEEK LESSON PLAN',
  title: 'THE WHOLE YEAR, MAPPED WEEK BY WEEK.',
  sub: '★ weeks start a new lesson. ◆ weeks are level checks. The weeks in between have a lesson focus and a ready 10-minute exercise or game.',
  quarters: [
    {
      q: 'Q1',
      weeks: 'WEEKS 1–13',
      name: 'BUILD THE BASE',
      full: [
        { week: '1', label: 'START LESSON 01', title: 'Safety, Mounting & a Correct Position' },
        { week: '5', label: 'START LESSON 02', title: 'Walk On, Halt & Steering' },
        { week: '9', label: 'START LESSON 03', title: 'Riding in a Group' },
        { week: '13', label: 'LEVEL CHECK', title: 'Check progress with the planner', review: true },
      ],
      activities: '+ ready 10-minute exercises and games, like Cone Weave, Halt Practice and Figure-8.',
    },
    {
      q: 'Q2',
      weeks: 'WEEKS 14–26',
      name: 'RIDE THE TROT',
      full: [
        { week: '14', label: 'START LESSON 04', title: 'Rising Trot' },
        { week: '18', label: 'START LESSON 05', title: 'School Figures' },
        { week: '22', label: 'START LESSON 06', title: 'Transitions: Walk–Trot–Walk' },
        { week: '26', label: 'LEVEL CHECK', title: 'Check progress with the planner', review: true },
      ],
      activities: '+ ready 10-minute exercises and games, like Trot Tempo Tracker, Corner Balance Game and Trot Transition Taps.',
    },
    {
      q: 'Q3',
      weeks: 'WEEKS 27–39',
      name: 'RHYTHM & BALANCE',
      full: [
        { week: '27', label: 'START LESSON 07', title: 'Ground Poles: Rhythm & Straightness' },
        { week: '31', label: 'START LESSON 08', title: 'Two-Point Position & Sitting Trot' },
        { week: '35', label: 'START LESSON 09', title: 'First Canter' },
        { week: '39', label: 'LEVEL CHECK', title: 'Check progress with the planner', review: true },
      ],
      activities: '+ a ready 10-minute exercise or game for the weeks in between.',
    },
    {
      q: 'Q4',
      weeks: 'WEEKS 40–52',
      name: 'PUT IT ALL TOGETHER',
      full: [
        { week: '40', label: 'START LESSON 10', title: 'Level Check Lesson' },
        { week: '52', label: 'LEVEL CHECK', title: 'Year-end level check', review: true },
      ],
      activities: '+ a ready 10-minute exercise or game for the weeks in between.',
    },
  ],
  toolsTitle: 'PLUS YOUR INSTRUCTOR TOOLS',
  tools: ['When the lesson gets hard: 8 situations, what to say and do', 'Lesson planner + level check tracker: plan any lesson in 5 minutes', 'Lesson field card to keep in the arena'],
};

// ── 7. THE 10 COMPLETE LESSONS ─────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE LESSONS',
  title: 'TEN GROUP LESSONS. READY TO TEACH.',
  sub: 'Follow the 52-week plan, or start with the lesson your riders need now. Each one is 3 pages: lesson plan, teach it, ride it.',
  weekLabel: 'START WEEK',
  items: [
    { n: '01', week: '1', title: 'Safety, Mounting & a Correct Position', goal: 'Check the tack, mount from the block, sit in the ear–shoulder–hip–heel line and dismount safely.' },
    { n: '02', week: '5', title: 'Walk On, Halt & Steering', goal: 'Walk on with a light aid, halt with the seat and soft hands and steer through cones.' },
    { n: '03', week: '9', title: 'Riding in a Group: Distance, Passing & Leading File', goal: 'Keep a horse length, lead the ride for a lap and pass left hand to left hand.' },
    { n: '04', week: '14', title: 'Rising Trot', goal: 'Rise and sit in rhythm for one long side and come back to walk with control.' },
    { n: '05', week: '18', title: 'School Figures: Circles, Corners & Changes of Rein', goal: 'A round 20 m circle, balanced corners and a change of rein across the diagonal.' },
    { n: '06', week: '22', title: 'Transitions: Walk–Trot–Walk', goal: 'Prepared walk–trot and trot–walk transitions at a chosen letter, with light aids.' },
    { n: '07', week: '27', title: 'Ground Poles: Rhythm & Straightness', goal: 'A straight line over 3 ground poles at walk and rising trot, with the eyes up.' },
    { n: '08', week: '31', title: 'Two-Point Position & Sitting Trot', goal: 'A balanced two-point at walk and trot, and a few strides of relaxed sitting trot.' },
    { n: '09', week: '35', title: 'First Canter', goal: 'Prepare in the corner, canter with clear aids and come back to trot calmly, one rider at a time.' },
    { n: '10', week: '40', title: 'Level Check Lesson', goal: 'A calm practice level check of the year’s skills, with a score sheet and a feedback script.' },
  ],
  cta: 'GET ALL 10 LESSONS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 BOOK',
  title: 'EVERYTHING ELSE YOUR RIDING LESSONS NEED.',
  sub: 'Arena exercises, rainy-day lessons, level checks, parent messages and mounted games, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '50 Arena Exercises & Patterns',
      hook: 'Never run out of arena ideas.',
      title: '50 Arena Exercises & Patterns — With Easy Diagrams',
      text: '50 ready-to-ride exercises organized by goal: steering and accuracy, rhythm, transitions, school figures, ground poles and group riding. Each with a simple diagram, the setup, how to explain it and an easier and a harder version.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: '10 Rainy-Day Lessons',
      hook: 'Bad weather? The lesson still happens.',
      title: '10 Rainy-Day Lessons — Unmounted Classes for When You Can’t Ride',
      text: '10 complete unmounted lessons: grooming and tacking up, parts of the horse, behavior and safety, feeding and stable care, leading, the aids explained, arena rules and a fun stable quiz.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'Rider Level Check Kit',
      hook: 'Show riders and parents real progress.',
      title: 'Rider Level Check Kit — Checklists, Progress Cards & Printable Certificates',
      text: 'Simple level checklists from the first lesson to a confident canter (adapt them to your riding school), a 10-minute level check plan, progress cards, an attendance tracker and printable certificates.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: 'Parent & New Rider Pack',
      hook: 'Know exactly what to send to families.',
      title: 'Parent & New Rider Pack — Welcome Letter, Yard Rules & Ready-to-Send Messages',
      text: 'A welcome letter for new riders, one-page yard and arena rules, a what-to-wear checklist, booking and cancellation messages, bad-weather notes and calm scripts for talking with parents.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: '40 Mounted Games for Kids',
      hook: 'Fun, safe games for lessons, camps and pony days.',
      title: '40 Mounted Games for Kids — For Lessons, Camps & Pony Days',
      text: 'Steering and balance games, relays at walk and trot, follow-the-leader patterns, cone and pole courses and team challenges. Each with the setup, the rules, the time, the level and a safety tip.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT LESSON STARTS HERE',
  title: 'YOUR WHOLE YEAR OF RIDING LESSONS. ALL IN ONE BOOK.',
  kicker: 'THE COMPLETE DIGITAL BOOK',
  name: 'Riding Instructor Ready™',
  description: 'A 52-week lesson plan and 10 complete, ready-to-teach 45-minute group riding lessons, plus five bonuses: arena exercises, rainy-day lessons, a level check kit, parent messages and mounted games.',
  mockupAlt: 'Everything included in Riding Instructor Ready™',
  includedTitle: 'YOUR BOOK INCLUDES',
  included: [
    '52-week lesson plan: the whole year mapped',
    '10 complete group lessons, 3 pages each',
    'Lesson goal, what you need and SAY THIS words',
    'Teaching steps, FIX IT cards and rider level cards',
    'Arena exercises, games and horse care tips',
    'Instructor tools: hard moments, lesson planner, field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Riding Instructor Ready™ (52-week plan + 10 complete lessons)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout (new copy for this landing) */
  cta: 'SEND ME MY RIDING YEAR — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. Adapt every lesson to your horses, your riding school’s safety rules and the level of your riders.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Teach your next lessons with it. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOU MOUNT UP.',
  sub: 'Built for instructors and riding school owners who teach group lessons. A ready-to-teach lesson book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF book: a 52-week lesson plan (every week has the start of a lesson, a lesson focus with a 10-minute exercise or game, or a level check) and 10 complete group lessons of 3 pages each, plus instructor tools and 5 bonuses.',
    },
    {
      q: 'Who is it for?',
      a: 'Riding instructors, assistant instructors and riding school owners who teach group lessons to beginner and novice riders (kids from about 8, teens and adults) in general English-style riding: flatwork and ground poles.',
    },
    {
      q: 'What level are the riders?',
      a: 'From the first lesson to a first canter. Every lesson has rider level cards for Level 1 (walk), Level 2 (walk and trot) and Level 3 (walk, trot and canter), so it works for mixed groups too.',
    },
    {
      q: 'Is there any jumping?',
      a: 'No. The lessons are flatwork and ground poles only. There is no jumping and no work with young or difficult horses.',
    },
    {
      q: 'What about safety?',
      a: 'Every lesson follows the basics: approved helmets and boots, a tack check before mounting, one horse length or more, left hand to left hand and one rider cantering at a time. Always follow your riding school’s safety rules.',
    },
    {
      q: 'Does it work for western riding?',
      a: 'It is written for general English-style riding. Many exercises work in any arena, but the terms and positions follow English-style flatwork.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I use the lessons individually?',
      a: 'Yes. Each lesson stands alone, so you can start with the one your riders need now, or follow the 52-week plan from week 1.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet in the arena, and the lesson planner and level check sheet are ready to print.',
    },
    {
      q: 'Can I share it with my instructors?',
      a: 'This purchase is for your own use as an instructor. Sharing it across several riding schools, redistributing it or reselling it is not included in this offer.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO WE WORK ON TODAY?”',
  title: 'WALK INTO THE ARENA WITH TODAY’S LESSON READY.',
  text: 'Open this week’s page. Prepare in 5 minutes. Teach a lesson your riders want to come back to.',
  microcopy: '52-week plan + 10 complete lessons + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'RIDING INSTRUCTOR READY™ · Lesson material for riding instructors. Always follow your riding school’s safety rules.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
